<template>
  <div class="content mb-5">
    <b-row class="mb-3 d-flex align-items-center">
      <b-col md="8">
        <h2 class="mb-0">
          {{ this.$t("Today's Web Traffic") }}
        </h2>
      </b-col>
      <b-col md="4">
        <h2 class="text-truncate" v-if="!loading">
          {{ customerName }}
        </h2>
        <!-- <b-skeleton
          v-else
          animation="wave"
          height="35px"
          width="100%"
        ></b-skeleton> -->
        <span v-else>
          <PuSkeleton height="35px" width="100%"> </PuSkeleton>
        </span>
      </b-col>
    </b-row>

    <b-row class="mb-5">
      <b-col cols="12" md="8" class="pl-2">
        <t-card
          v-if="!loadingTraffic"
          size="large"
          highlight-value="123"
          :title="this.$t('Web Traffic')"
        >
          <div class="text-center p-5 m-2" v-if="loadingTraffic">
            <b-spinner
              class="my-5"
              variant="primary"
              :label="this.$t('Spinning')"
            ></b-spinner>
          </div>
          <t-chart-wrapper
            v-if="!loadingTraffic"
            size="large"
            chart-type="line"
            with-scales
            :chart-data="webTraffic"
            @generate-legend="generateLegend"
          />

          <div v-html="chartLegend"></div>
        </t-card>
        <span v-if="loadingTraffic">
          <PuSkeleton height="350px"> </PuSkeleton>
        </span>

        <!-- <b-skeleton-img v-if="loadingTraffic" height="350px"></b-skeleton-img> -->
      </b-col>
      <b-col cols="12" md="4" class="pr-sm-2">
        <t-quick-actions
          :title="this.$t('Quick Actions')"
          :actions="quickActions"
          :loading="loadingTraffic"
          @on-action-click="onQuickActionClick"
        />
      </b-col>
    </b-row>

    <b-row class="mb-3 d-flex align-items-center">
      <b-col cols="12" sm="8">
        <h2 class="mb-0">
          {{ this.$t("Your Protection and Filtering at a Glance") }}
        </h2>
      </b-col>
      <b-col cols="12" sm="4" class="d-flex justify-content-end">
        <b-form-datepicker
          id="example-datepicker"
          v-model="selectedDate"
          @input="getUserDashboard()"
          :max="currentDate"
          class="mb-0"
        ></b-form-datepicker>
      </b-col>
    </b-row>

    <section :class="['customer', { loading: loadingStats && !loading }]">
      <b-row class="mb-2">
        <b-col cols="12" sm="6" md="6" lg="3" class="mb-3 pr-md-2">
          <t-card-trend
            data-test-id="dashboard"
            :title="this.$t('Requests')"
            :figure="
              customerStats.overview.requests !== undefined
                ? customerStats.overview.requests
                : 0
            "
            :loading="loading"
          />
        </b-col>
        <b-col cols="12" sm="6" md="6" lg="3" class="mb-3 px-md-2">
          <t-card-trend
            data-test-id="dashboard"
            :title="this.$t('Unique Domains')"
            :figure="
              customerStats.overview.unique_domains !== undefined
                ? customerStats.overview.unique_domains
                : 0
            "
            :loading="loading"
          />
        </b-col>
        <b-col cols="12" sm="6" md="6" lg="3" class="mb-3 px-md-2">
          <t-card-trend
            data-test-id="dashboard"
            :title="this.$t('Allowed')"
            :figure="
              customerStats.overview.allowed !== undefined
                ? customerStats.overview.allowed
                : 0
            "
            :loading="loading"
          />
        </b-col>
        <b-col cols="12" sm="6" md="6" lg="3" class="mb-3 pl-md-2">
          <t-card-trend
            data-test-id="dashboard"
            :title="this.$t('Blocked')"
            :figure="
              customerStats.overview.blocked !== undefined
                ? customerStats.overview.blocked
                : 0
            "
            :loading="loading"
          />
        </b-col>
      </b-row>

      <b-row>
        <b-col
          sm="12"
          lg="6"
          class="mb-4 pr-2"
          v-if="customerStats.topCategories.allowed.count"
        >
          <b-card no-body class="p-4" v-if="!loadingCategories">
            <h4 class="mb-3">{{ this.$t("Top Allowed Categories") }}</h4>
            <b-row>
              <b-col cols="12" md="6">
                <b-list-group class="mb-0">
                  <b-list-group-item
                    class="border-0 py-1 px-0"
                    v-for="(item, idx) in topCategoriesAllowed"
                    :key="idx"
                  >
                    <span
                      class="chart-color-ref mr-2"
                      :style="`background: ${chartColours[idx]}`"
                    ></span
                    >{{ item.name }}
                  </b-list-group-item>
                </b-list-group>
              </b-col>
              <b-col cols="8" offset="2" offset-md="1" md="4" class="mb-0 p-0">
                <t-chart-wrapper
                  chart-type="doughnut"
                  size="auto"
                  :chart-data="topChartDataset(topCategoriesAllowed)"
                />
              </b-col>
            </b-row>
          </b-card>
          <span v-if="loadingCategories">
            <PuSkeleton height="150px"> </PuSkeleton>
          </span>
        </b-col>
        <b-col
          sm="12"
          lg="6"
          class="mb-4 pl-2"
          v-if="customerStats.topCategories.blocked.count"
        >
          <b-card no-body class="p-4" v-if="!loadingCategories">
            <h4 class="mb-3">{{ this.$t("Top Blocked Categories") }}</h4>
            <b-row>
              <b-col cols="12" md="6">
                <b-list-group class="mb-0">
                  <b-list-group-item
                    class="border-0 py-1 px-0"
                    v-for="(item, idx) in topCategoriesBlocked"
                    :key="idx"
                  >
                    <span
                      class="chart-color-ref mr-2"
                      :style="`background: ${chartColours[idx]}`"
                    ></span
                    >{{ item.name }}
                  </b-list-group-item>
                </b-list-group>
              </b-col>
              <b-col cols="8" offset="2" offset-md="1" md="4" class="mb-0 p-0">
                <t-chart-wrapper
                  chart-type="doughnut"
                  size="auto"
                  :chart-data="topChartDataset(topCategoriesBlocked)"
                />
              </b-col>
            </b-row>
          </b-card>
          <span v-if="loadingCategories">
            <PuSkeleton height="150px"> </PuSkeleton>
          </span>
          <!-- <b-skeleton-img
            v-if="loadingCategories"
            height="150px"
          ></b-skeleton-img> -->
        </b-col>
      </b-row>

      <b-row class="mb-4">
        <b-col
          cols="12"
          md="6"
          class="pr-2"
          v-if="customerStats.topDomains.allowed.count"
        >
          <b-card no-body v-if="!loadingDomains">
            <h4 class="p-4 mb-0">{{ this.$t("Top Allowed Domains") }}</h4>
            <t-table
              :table-data="{
                fields: [],
                items: topDomains('allowed'),
              }"
            />
          </b-card>
          <span v-if="loadingDomains">
            <PuSkeleton height="150px"> </PuSkeleton>
          </span>
          <!-- <b-skeleton-img v-if="loadingDomains" height="150px"></b-skeleton-img> -->
        </b-col>
        <b-col
          cols="12"
          md="6"
          class="pl-2"
          v-if="customerStats.topDomains.blocked.count"
        >
          <b-card no-body v-if="!loadingDomains">
            <h4 class="p-4 mb-0">{{ this.$t("Top Blocked Domains") }}</h4>
            <t-table
              :table-data="{
                fields: [],
                items: topDomains('blocked'),
              }"
            />
          </b-card>
          <span v-if="loadingDomains">
            <PuSkeleton height="150px"> </PuSkeleton>
          </span>
          <!-- <b-skeleton-img v-if="loadingDomains" height="150px"></b-skeleton-img> -->
        </b-col>
      </b-row>

      <b-row v-if="!loadingUsers">
        <b-col
          sm="12"
          lg="6"
          class="mb-3 pr-2"
          v-if="customerStats.topUsers.allowed.count"
        >
          <t-card
            v-if="!loading"
            actions-layout="button-group"
            :title="this.$t('Top Allowed Users')"
          >
            <b-row>
              <b-col cols="12" md="7">
                <b-list-group class="mb-3 sm-mb-0">
                  <b-list-group-item
                    class="border-0 py-1"
                    v-for="(item, idx) in customerStats.topUsers.allowed.data"
                    :key="idx"
                  >
                    <span
                      class="chart-color-ref mr-2"
                      :style="`background: ${chartColours[idx]}`"
                    ></span
                    >{{ item.name }}
                  </b-list-group-item>
                </b-list-group>
              </b-col>
              <b-col
                cols="8"
                offset="2"
                offset-md="0"
                md="4"
                class="mb-3 sm-mb-0 p-0"
              >
                <t-chart-wrapper
                  chart-type="doughnut"
                  size="auto"
                  :chart-data="
                    topChartDataset(customerStats.topUsers.allowed.data)
                  "
                />
              </b-col>
            </b-row>
          </t-card>
          <span v-if="loadingUsers">
            <PuSkeleton height="150px"> </PuSkeleton>
          </span>
          <!-- <b-skeleton-img v-if="loadingUsers" height="150px"></b-skeleton-img> -->
        </b-col>
        <b-col
          sm="12"
          lg="6"
          class="mb-3 pl-2"
          v-if="customerStats.topUsers.blocked.count"
        >
          <t-card
            v-if="!loadingUsers"
            actions-layout="button-group"
            :title="this.$t('Top Blocked Users')"
          >
            <b-row>
              <b-col cols="12" md="7">
                <b-list-group class="mb-3 sm-mb-0">
                  <b-list-group-item
                    class="border-0 py-1"
                    v-for="(item, idx) in customerStats.topUsers.blocked.data"
                    :key="idx"
                  >
                    <span
                      class="chart-color-ref mr-2"
                      :style="`background: ${chartColours[idx]}`"
                    ></span
                    >{{ item.name }}
                  </b-list-group-item>
                </b-list-group>
              </b-col>
              <b-col
                cols="8"
                offset="2"
                offset-md="0"
                md="4"
                class="mb-3 sm-mb-0 p-0"
              >
                <t-chart-wrapper
                  chart-type="doughnut"
                  size="auto"
                  :chart-data="
                    topChartDataset(customerStats.topUsers.blocked.data)
                  "
                />
              </b-col>
            </b-row>
          </t-card>
          <span v-if="loadingUsers">
            <PuSkeleton height="150px"> </PuSkeleton>
          </span>
          <!-- <b-skeleton-img v-if="loadingUsers" height="150px"></b-skeleton-img> -->
        </b-col>
      </b-row>
    </section>
  </div>
</template>

<route>
{
  "name": "Customer Overview",
  "meta": {
    "authorize" : [1, 3, 4]
  }
}
</route>

<script>
import { mapActions, mapGetters, mapMutations } from "vuex";
import dayjs from "dayjs";

export default {
  layout: "customer",
  data() {
    return {
      customerName: null,
      chartLegend: "",
      selectedDate: "",
      currentDate: "",
      loadingStats: true,
      loading: true,
      loadingTraffic: true,
      loadingCategories: true,
      loadingDomains: true,
      loadingUsers: true,
      chartColours: [
        "#058DC7",
        "#50b432",
        "#ed561b",
        "#edef00",
        "#24cbe5",
        "#64e572",
        "#ff9655",
        "#fff263",
        "#6af9c4",
        "#b2deff",
      ],
      quickActions: [
        {
          title: this.$t("Add Location"),
          testID: "dashboard-quick-actions-add-location",
          value: "addLocation",
          icon: "geo-alt",
          iconVariant: "secondary",
        },
        {
          title: this.$t("Allow Domain"),
          testID: "dashboard-quick-actions-allow-domain",
          value: "allowDomain",
          icon: "check-square",
          iconVariant: "secondary",
        },
        {
          title: this.$t("Block Domain"),
          testID: "dashboard-quick-actions-block-domain",
          value: "blockDomain",
          icon: "x-square",
          iconVariant: "secondary",
        },
        {
          title: this.$t("Customise Policy"),
          testID: "dashboard-quick-actions-customise-policy",
          value: "customisePolicy",
          icon: "shield",
          iconVariant: "secondary",
        },
      ],
    };
  },
  methods: {
    ...mapActions("stats", [
      "fetchUserDashboard",
      "fetchCustomerOverview",
      "fetchCustomerTopCategories",
      "fetchCustomerRecentTraffic",
      "fetchCustomerTopDomains",
      "fetchCustomerTopUsers",
    ]),
    ...mapActions("pageMeta", ["setPageTitle", "setPageSubtitle"]),
    ...mapActions("customerPolicies", ["fetchCustomerPolicies"]),
    ...mapMutations("modal", ["createModal", "showModal"]),
    generateLegend(legend) {
      const clean = this.$sanitize(legend, {
        allowedTags: ["p", "span", "div", "strong"],
        allowedClasses: {
          "*": [
            "t-legend",
            "text-center",
            " my-2",
            "d-inline-flex",
            "align-items-center",
            "mr-5",
            "mr-1",
            "d-inline-block",
            "rounded-circle",
          ],
        },
        allowedAttributes: {
          span: ["style"],
        },
        allowedStyles: {
          "*": {
            // Match HEX and RGB
            color: [
              /^#(0x)?[0-9a-f]+$/i,
              /^rgb\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})\s*\)$/,
            ],
            "text-align": [/^left$/, /^right$/, /^center$/],
            // Match any number with px, em, or %
            "font-size": [/^\d+(?:px|em|%)$/],
          },
          span: {
            background: [
              /^#(0x)?[0-9a-f]+$/i,
              /^rgb\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})\s*\)$/,
            ],
            width: [/^\d+(?:px|rem|%)$/],
            height: [/^\d+(?:px|rem|%)$/],
          },
        },
      });
      this.chartLegend = clean;
    },
    onQuickActionClick(action) {
      switch (action.value) {
        case "allowDomain":
          this.createModal({
            modal: "t-add-domain",
            title: this.$t("Allow a Domain"),
            dataTestId: `modal-add-domain-allow`,
            subtitle: this.selectedUser.accountname,
            modalData: {
              type: "allow",
              customerPolicies: this.customerPolicies,
              toPolicy: false,
              policies: this.customerPolicies,
            },
            props: {
              size: "lg",
              okTitle: `${this.$t("Allow")} ${this.$t("Domain")}`,
            },
          });
          this.showModal();
          break;

        case "blockDomain":
          this.createModal({
            modal: "t-add-domain",
            title: this.$t("Block a Domain"),
            dataTestId: `modal-add-domain-block`,
            subtitle: this.selectedUser.accountname,
            modalData: {
              type: "block",
              customerPolicies: this.customerPolicies,
              toPolicy: false,
              policies: this.customerPolicies,
            },
            props: {
              size: "lg",
              okTitle: `${this.$t("Block")} ${this.$t("Domain")}`,
            },
          });
          this.showModal();
          break;

        case "blockLocation":
        case "addLocation":
          this.$router.push("/customer/environment/locations");
          break;

        case "customisePolicy":
          this.$router.push("/customer/content_filtering/policies");
          break;

        default:
          break;
      }
    },
    topChartDataset(dataset) {
      return {
        labels: dataset.map((item) => item.name),
        datasets: [
          {
            data: dataset.map((item) => item.count),
            backgroundColor: this.chartColours,
            hoverOffset: 4,
          },
        ],
      };
    },
    organiseWebTrafficChart(data) {
      if (!data) return [];
      return {
        labels: data.map((item) => dayjs(item.date).format("hh:mm")),
        datasets: [
          {
            label: this.$t("Allowed"),
            borderColor: this.$chartColours.allowed,
            backgroundColor: this.$chartColours.allowed,
            data: data.map((item) => item.allowed),
            fill: false,
          },
          {
            label: this.$t("Blocked"),
            borderColor: this.$chartColours.blocked,
            backgroundColor: this.$chartColours.blocked,
            data: data.map((item) => item.blocked),
            fill: false,
          },
        ],
      };
    },
    async getWebTraffic() {
      this.loadingTraffic = true;
      try {
        await this.fetchCustomerRecentTraffic({
          id: this.selectedUser.id,
          day: "today",
        });
      } finally {
        this.loadingTraffic = false;
      }
    },
    async getTopCategories() {
      this.loadingCategories = true;
      try {
        await this.fetchCustomerTopCategories({
          id: this.selectedUser.id,
          type: "allowed",
          date: this.selectedDate,
        });
        await this.fetchCustomerTopCategories({
          id: this.selectedUser.id,
          type: "blocked",
          date: this.selectedDate,
        });
      } finally {
        this.loadingCategories = false;
      }
    },
    async getTopDomains() {
      this.loadingDomains = true;
      try {
        await this.fetchCustomerTopDomains({
          id: this.selectedUser.id,
          type: "allowed",
          date: this.selectedDate,
        });
        await this.fetchCustomerTopDomains({
          id: this.selectedUser.id,
          type: "blocked",
          date: this.selectedDate,
        });
      } finally {
        this.loadingDomains = false;
      }
    },
    async getTopUsers() {
      this.loadingUsers = true;
      try {
        await this.fetchCustomerTopUsers({
          id: this.selectedUser.id,
          type: "allowed",
          date: this.selectedDate,
        });
        await this.fetchCustomerTopUsers({
          id: this.selectedUser.id,
          type: "blocked",
          date: this.selectedDate,
        });
      } finally {
        this.loadingUsers = false;
      }
    },
    async getCustomerOverview() {
      this.loadingStats = true;
      try {
        if (!this.selectedUser.allCustomers) {
          await this.fetchCustomerOverview({
            id: this.selectedUser.id,
            date: this.selectedDate,
          });
        }
      } finally {
        this.loading = false;
        this.loadingStats = false;
      }
    },
    async getUserDashboard() {
      await this.getWebTraffic();
      await this.getTopCategories();
      await this.getTopDomains();
      await this.getTopUsers();
      await this.getCustomerOverview();
    },
    topDomains(type) {
      if (type === "allowed") {
        return this.customerStats.topDomains.allowed.data.length > 0
          ? this.customerStats.topDomains.allowed.data.map((domain) => ({
              ...domain,
              name: this.$formatShortDomain(domain.name),
            }))
          : [];
      } else {
        return this.customerStats.topDomains.blocked.data.length > 0
          ? this.customerStats.topDomains.blocked.data.map((domain) => ({
              ...domain,
              name: this.$formatShortDomain(domain.name),
            }))
          : [];
      }
    },
  },
  computed: {
    ...mapGetters("stats", ["customerStats", "dashboard", "customerStats"]),
    ...mapGetters("authentication", ["selectedUser", "authUser", "role"]),
    ...mapGetters("customerPolicies", ["customerPolicies"]),
    webTraffic() {
      if (this.customerStats.recentTraffic.data.length) {
        return (
          this.customerStats.recentTraffic.data &&
          this.organiseWebTrafficChart(this.customerStats.recentTraffic.data)
        );
      }
      return [];
    },
    topCategoriesAllowed() {
      if (this.customerStats.topCategories.allowed.data.length) {
        return this.customerStats.topCategories.allowed.data.slice(0, 5);
      }
      return [];
    },
    topCategoriesBlocked() {
      if (this.customerStats.topCategories.blocked.data.length) {
        return this.customerStats.topCategories.blocked.data.slice(0, 5);
      }
      return [];
    },
    todaysDate() {
      return dayjs(this.selectedDate).format("dddd, D MMMM YYYY ");
    },
  },
  async created() {
    this.setPageTitle(this.$t("Overview") + "  ");
    this.setPageSubtitle([]);
    this.selectedDate = dayjs()
      .tz(this.selectedUser.timezone)
      .format("YYYY-MM-DD");
    this.currentDate = this.selectedDate;
    try {
      if (!this.selectedUser.allCustomers) {
        await this.getUserDashboard();
        if (this.selectedUser.id)
          await this.fetchCustomerPolicies({
            id: this.selectedUser.id,
            show_default: "false",
            results: 10,
            sortDesc: [false],
          });
      }
    } finally {
      this.loading = false;
    }

    if (this.role === 4) {
      this.customerName = this.authUser.accountname;
    } else {
      this.customerName = this.selectedUser.accountname;
    }
  },
};
</script>

<style scoped lang="scss">
.chart-color-ref {
  width: 12px;
  height: 12px;
  display: inline-block;
}

.customer.loading {
  position: relative;
  &::after {
    content: "";
    display: block;
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    z-index: $z-farthest;
    background: rgba(255, 255, 255, 0.548);
  }
}
.card {
  height: 100%;
}
</style>
